import Base from '../../Base/Base';
import { useContext, useEffect, useState } from 'react';
import { Button } from '@upmc/ims.component-library';
import Styles from './GlobalProtectExceptionForm.module.scss';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import TextField from '@mui/material/TextField';
import MaterialIcon from '@material/react-material-icon';
import { SubmitException } from './GlobalProtectException_api';
import { useFormik } from 'formik';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { UserContext } from 'context/User/UserContext';
import Yup from 'utils/validation/yup-extensions';

const theme = createTheme({
	palette: {
		primary: {
			// Purple and green play nicely together.
			main: '#771b61',
		},
	},
});

/**
 * Global Protect Form
 *
 * @returns rendered component.
 */

const GlobalProtectExceptionForm = ({
	userSelected,
	setStep,
	setTrackingNumber,
}) => {
	const [formBody, setFormBody] = useState({
		Name: '',
		NTID: '',
		Esignon: '',
		Email: '',
		Department: '',
		Manager: '',
		Requestee_ESOFIID: '',
		Requestor_ESOFIID: '',
		VPNSoftware: '',
		IPAddress: '',
		Description: '',
	});

	const validationSchema = Yup.object({
		VPNSoftware: Yup.string('Enter the name of your VPN Client Software')
			.required('VPN Client Software is required')
			.max(256, 'Maximum length of 256 characters'),
		IPAddress: Yup.string('Enter URL or IP or FQDN')
			.required('URL or IP or FQDN Required')
			.max(256, 'Maximum length of 256 characters'),
		Description: Yup.string('Enter your Description of Access').max(
			256,
			'Maximum length of 256 characters'
		),
	});

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	const [submissionSending, setSubmissionSending] = useState(false);
	const [submissionError, setSubmissionError] = useState('');

	useEffect(() => {
		setFormBody({ ...userSelected });
	}, [userSelected]);

	// Only set this once if the user is not selected(meaning we selected 'myself' as the option)
	// and if the formBody is not already set with the NTID. After we select the 'myself' option the user will not change
	if (!userSelected && !formBody?.NTID && user) {
		setFormBody(prevFormBody => ({
			...prevFormBody,
			Name: user?.Name,
			NTID: user?.NetworkID,
			Esignon: user?.FamiliarESOID,
			Requestee_ESOFIID: user?.ID,
			Requestor_ESOFIID: user?.ID,
			Email: user?.Email,
			Department: user?.Department,
			Manager: user?.Manager,
		}));
	}

	const SubmitGlobalProtectException = requiredValues => {
		setSubmissionSending(true);
		setFormBody({
			...formBody,
			Requestor_ESOFIID: user?.ID,
			VPNSoftware: requiredValues.VPNSoftware,
			IPAddress: requiredValues.IPAddress,
			Description: requiredValues.Description,
		});

		// Set local variable because state is not updated yet
		var submitArgs = {
			...formBody,
			Requestor_ESOFIID: user?.ID,
			VPNSoftware: requiredValues.VPNSoftware,
			IPAddress: requiredValues.IPAddress,
			Description: requiredValues.Description,
		};
		SubmitException(submitArgs).then(resp => {
			const trackingNumber = resp?.data?.payload;
			// Only advance on a successful save; a failed submission returns an
			// error or no tracking number.
			if (resp?.error || resp?.data?.responseCode === 500 || !trackingNumber) {
				setSubmissionError(
					'Your request could not be submitted. Please try again.'
				);
				setSubmissionSending(false);
				return;
			}
			if (setTrackingNumber) {
				setTrackingNumber(trackingNumber);
			}
			setSubmissionError('');
			setStep();
			setSubmissionSending(false);
		});
	};

	const formik = useFormik({
		initialValues: {
			VPNSoftware: '',
			IPAddress: '',
			Description: '',
		},
		validationSchema: validationSchema,
		onSubmit: values => {
			SubmitGlobalProtectException(values);
		},
	});

	return (
		<Base
			title={`Exception for ${userSelected ? 'Other' : 'Self'}`}
			className={Styles['base--default']}
		>
			<formik>
				<form onSubmit={formik.handleSubmit}>
					<StyleAuxiliary>
						<ThemeProvider theme={theme}>
							<Grid container sx={{ marginBottom: '16px' }}>
								<Grid item xs={12}>
									<p style={{ margin: 0 }}>
										This page allows users to request an
										exception to using the standard Global
										Protect VPN (vitual private network)
										remote access for UPMC devices.
									</p>
								</Grid>
							</Grid>
							<Grid
								container
								direction='row'
								spacing={1}
								sx={{
									alignItems: 'center'
								}}
							>
								<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
									<TextField
										disabled
										fullWidth
										value={formBody.Name}
										id='outlined-name-input'
										label='Name'
										type='text'
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
									<TextField
										disabled
										value={formBody.NTID}
										fullWidth
										id='outlined-networkID-input'
										label='Network ID'
										type='text'
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
									<TextField
										disabled
										value={formBody.Esignon}
										fullWidth
										id='outlined-esignon-input'
										label='ESignOn'
										type='text'
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
									<TextField
										disabled
										value={formBody.Email}
										fullWidth
										id='outlined-email-input'
										label='Email'
										type='text'
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
									<TextField
										disabled
										value={formBody.Department}
										fullWidth
										id='outlined-department-input'
										label='Department'
										type='text'
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={6} lg={4} xl={4}>
									<TextField
										disabled
										value={formBody.Manager}
										fullWidth
										id='outlined-manager-input'
										label='Manager'
										type='text'
									/>
								</Grid>
								<Grid
									item
									xs={12}
									sm={12}
									md={12}
									lg={12}
									xl={12}
								>
									<TextField
										value={formik.values.VPNSoftware}
										onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									name='VPNSoftware'
									error={
										formik.values.VPNSoftware.length >= 256 ||
										((formik.touched.VPNSoftware ||
											formik.submitCount > 0) &&
											Boolean(formik.errors.VPNSoftware))
									}
									helperText={
										formik.values.VPNSoftware.length >= 256
											? 'Maximum length of 256 characters'
											: (formik.touched.VPNSoftware ||
													formik.submitCount > 0) &&
												formik.errors.VPNSoftware
									}
									inputProps={{ maxLength: 256 }}
										fullWidth
										id='outlined-clientsoftware-input'
										label='Name of Client Software'
										type='text'
									/>
								</Grid>
								<Grid
									item
									xs={12}
									sm={12}
									md={12}
									lg={12}
									xl={12}
								>
									<TextField
										value={formik.values.IPAddress}
										onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									name='IPAddress'
									error={
										formik.values.IPAddress.length >= 256 ||
										((formik.touched.IPAddress ||
											formik.submitCount > 0) &&
											Boolean(formik.errors.IPAddress))
									}
									helperText={
										formik.values.IPAddress.length >= 256
											? 'Maximum length of 256 characters'
											: (formik.touched.IPAddress ||
													formik.submitCount > 0) &&
												formik.errors.IPAddress
									}
									inputProps={{ maxLength: 256 }}
										fullWidth
										id='outlined-connection-input'
										label='Connection (URL/IP/FQDN)'
										type='text'
									/>
								</Grid>
								<Grid
									item
									xs={12}
									sm={12}
									md={12}
									lg={12}
									xl={12}
								>
									<TextField
										value={formik.values.Description}
										onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									name='Description'
									error={
										formik.values.Description.length >= 256 ||
										((formik.touched.Description ||
											formik.submitCount > 0) &&
											Boolean(formik.errors.Description))
									}
									helperText={
										formik.values.Description.length >= 256
											? 'Maximum length of 256 characters'
											: (formik.touched.Description ||
													formik.submitCount > 0) &&
												formik.errors.Description
									}
									inputProps={{ maxLength: 256 }}
										fullWidth
										id='outlined-description-input'
										label='Description of Access'
										type='text'
									/>
								</Grid>
							</Grid>
							<Grid
								container
								direction='column'
								spacing={3}
								sx={{alignItems: 'center'}}
							>
								<Grid item></Grid>
								{submissionError && (
									<Grid item>
										<p
											style={{
												margin: 0,
												color: '#d32f2f',
											}}
										>
											{submissionError}
										</p>
									</Grid>
								)}
								<Grid item>
									<Button
										type='submit'
										startIcon={
											submissionSending ? (
												<CircularProgress size={24} />
											) : (
												<MaterialIcon icon='save' />
											)
										}
										color='warning'
										variant='contained'
										disabled={submissionSending}
										sx={{ margin: '5px' }}
										id='SubmitGPException'
									>
										Submit Global Protect Exception
									</Button>
								</Grid>
							</Grid>
						</ThemeProvider>
					</StyleAuxiliary>
				</form>
			</formik>
		</Base>
	);
};

export default GlobalProtectExceptionForm;
